import React, { useState } from "react";
import { Trash2, Paperclip } from "lucide-react";
import toast from "react-hot-toast";
import { useQuestionOptions } from "./questionOptions";

export const RequiredMark = ({ show = false }) =>
  show ? <span className="text-red-500 ml-1">*</span> : null;

const QuestionInput = ({
  field,
  value,
  onChange,
  onBlur,
  onOptionsLoaded,
  disabled = false,
}) => {
  const [error, setError] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const { options, loading } = useQuestionOptions(field, onOptionsLoaded);
  const type = field?.questionType || field?.fieldType;

  const disabledClassName = disabled
    ? "bg-gray-100 cursor-not-allowed text-gray-500"
    : "";

  const isPercentage =
    type === "percentage" ||
    String(field?.questionText || field?.fieldLabel || "")
      .toLowerCase()
      .includes("percentage");

  if (loading) {
    return <span className="text-gray-400 text-sm">Loading options...</span>;
  }

  switch (type) {
    case "text":
      return (
        <input
          type="text"
          value={value || ""}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          onBlur={() => onBlur?.()}
          className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none ${disabledClassName}`}
          placeholder={`Enter ${field?.questionText || field?.fieldLabel || "value"}`}
        />
      );

    case "date":
      return (
        <input
          type="date"
          value={value || ""}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          onBlur={() => onBlur?.()}
          className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none ${disabledClassName}`}
        />
      );

    case "number":
    case "percentage": {
      const rangeLimit =
        Array.isArray(field?.limit) && field.limit.length > 0
          ? field.limit[0]
          : null;

      const showPercentageSuffix =
        isPercentage &&
        !isFocused &&
        value !== "" &&
        value !== null &&
        value !== undefined;
      const displayValue = showPercentageSuffix ? `${value}%` : (value ?? "");
      const inputType = showPercentageSuffix ? "text" : "number";

      return (
        <div className="relative w-full">
          <input
            type={inputType}
            min="0"
            value={displayValue}
            disabled={disabled}
            onChange={(e) => {
              let val = e.target.value;
              if (val !== "") {
                const numVal = Number(val);

                if (numVal < 0) {
                  toast.error("Negative values are not allowed");
                  return;
                }

                if (isPercentage && numVal > 100) {
                  toast.error("Percentage cannot exceed 100%");
                  val = "100";
                }

                if (
                  rangeLimit &&
                  typeof rangeLimit === "string" &&
                  rangeLimit.includes("-")
                ) {
                  const [min, max] = rangeLimit.split("-").map(Number);
                  if (numVal < min) {
                    toast.error(`Value cannot be less than ${min}`);
                    val = min.toString();
                  } else if (numVal > max) {
                    toast.error(`Value cannot exceed ${max}`);
                    val = max.toString();
                  }
                }
              }

              onChange(val);
            }}
            onFocus={() => setIsFocused(true)}
            onBlur={() => {
              setIsFocused(false);
              onBlur?.();
            }}
            className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none ${disabledClassName}`}
            placeholder={
              rangeLimit
                ? `Range ${rangeLimit}`
                : isPercentage
                  ? "Enter in percentage"
                  : "Enter value"
            }
          />
        </div>
      );
    }

    case "select":
      return (
        <select
          value={value || ""}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          onBlur={() => onBlur?.()}
          className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white outline-none ${disabledClassName}`}
        >
          <option value="">-- Select --</option>
          {options.map((opt) => (
            <option key={opt?._id || opt?.value} value={opt?.value}>
              {opt?.label}
            </option>
          ))}
        </select>
      );

    case "multiselect":
    case "multi-select": {
      const current = Array.isArray(value) ? value : [];
      const hasHeaders = options.some((opt) => !opt?.value || opt.value === "");

      if (!hasHeaders) {
        return (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {options.map((opt) => (
              <label
                key={opt?._id || opt?.value}
                className={`flex items-center gap-2 p-2 rounded ${
                  disabled
                    ? "cursor-not-allowed opacity-60"
                    : "cursor-pointer hover:bg-gray-50"
                }`}
              >
                <input
                  type="checkbox"
                  checked={current.includes(opt?.value)}
                  disabled={disabled}
                  onChange={(e) => {
                    if (e.target.checked) {
                      onChange([...current, opt?.value]);
                    } else {
                      onChange(current.filter((v) => v !== opt?.value));
                    }
                  }}
                  className="w-4 h-4 text-teal-600 rounded focus:ring-teal-500"
                />
                <span className="text-gray-700 text-sm">{opt?.label}</span>
              </label>
            ))}
          </div>
        );
      }

      const groupedOptions = [];
      let currentGroup = { header: null, options: [] };

      options.forEach((opt) => {
        if (!opt?.value || opt.value === "") {
          if (currentGroup.options.length > 0) {
            groupedOptions.push(currentGroup);
          }
          currentGroup = { header: opt?.label, options: [] };
        } else {
          currentGroup.options.push(opt);
        }
      });

      if (currentGroup.options.length > 0) {
        groupedOptions.push(currentGroup);
      }

      return (
        <div className="space-y-4">
          {groupedOptions.map((group, groupIdx) => (
            <div key={groupIdx} className="space-y-1">
              {group.header && (
                <div className="font-semibold text-gray-800 text-sm uppercase tracking-wide">
                  {group.header}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-0">
                {group.options.map((opt) => (
                  <label
                    key={opt?._id || opt?.value}
                    className={`flex items-center gap-2 p-1 rounded ${
                      disabled
                        ? "cursor-not-allowed opacity-60"
                        : "cursor-pointer hover:bg-gray-50"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={current.includes(opt?.value)}
                      disabled={disabled}
                      onChange={(e) => {
                        if (e.target.checked) {
                          onChange([...current, opt?.value]);
                        } else {
                          onChange(current.filter((v) => v !== opt?.value));
                        }
                      }}
                      className="w-4 h-4 text-teal-600 rounded focus:ring-teal-500 flex-shrink-0"
                    />
                    <span className="text-gray-700 text-sm">{opt?.label}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      );
    }

    case "file": {
      const files = Array.isArray(value) ? value : value ? [value] : [];

      return (
        <div className="space-y-3">
          <input
            type="file"
            disabled={disabled}
            onChange={(e) => {
              if (disabled) return;
              if (e.target.files && e.target.files.length > 0) {
                const maxBytes = 1 * 1024 * 1024; // 1 MB limit
                const newFiles = Array.from(e.target.files);
                const validFiles = [];
                let hasLargeFile = false;

                newFiles.forEach((file) => {
                  if (file.size > maxBytes) {
                    hasLargeFile = true;
                  } else {
                    validFiles.push(file);
                  }
                });

                if (hasLargeFile) {
                  setError("File size should be less than 1 mb");
                } else {
                  setError("");
                }

                if (validFiles.length > 0) {
                  onChange([...files, ...validFiles]);
                }
                e.target.value = null;
              }
            }}
            className={`w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-teal-50 file:text-teal-700 hover:file:bg-teal-100 ${
              disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"
            }`}
            multiple
          />
          {error && (
            <p className="text-xs text-red-500 font-medium mt-1">{error}</p>
          )}
          {files.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-700">
                Selected Files:
              </p>
              <ul className="space-y-1">
                {files.map((file, idx) => (
                  <li
                    key={idx}
                    className="flex items-center justify-between bg-white px-3 py-2 rounded-lg border border-gray-200 shadow-sm hover:border-teal-300 transition-all duration-200 hover:shadow-md"
                  >
                    <div
                      className="flex items-center gap-2 overflow-hidden cursor-pointer group"
                      onClick={() => {
                        if (file instanceof File) {
                          const fileURL = URL.createObjectURL(file);
                          window.open(fileURL, "_blank");
                        } else if (typeof file === "string") {
                          let fileURL = file;
                          if (
                            !file.startsWith("http://") &&
                            !file.startsWith("https://")
                          ) {
                            const apiBase = process.env.REACT_APP_API_URL || "";
                            const cleanBase = apiBase.endsWith("/")
                              ? apiBase.slice(0, -1)
                              : apiBase;
                            const cleanPath = file.startsWith("/")
                              ? file
                              : `/${file}`;
                            fileURL = `${cleanBase}${cleanPath}`;
                          }
                          window.open(fileURL, "_blank");
                        }
                      }}
                    >
                      <div className="flex items-center justify-center p-1.5 bg-teal-50 rounded-md text-teal-600 group-hover:bg-teal-100 transition-colors">
                        <Paperclip size={14} />
                      </div>
                      <span className="text-[13px] font-medium text-gray-700 group-hover:text-teal-700 truncate transition-colors">
                        {file?.name ||
                          (typeof file === "string"
                            ? file.split("/").pop()
                            : "View File")}
                      </span>
                    </div>
                    {!disabled && (
                      <button
                        type="button"
                        onClick={() =>
                          onChange(files.filter((_, i) => i !== idx))
                        }
                        className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors ml-3 flex-shrink-0"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      );
    }

    default:
      return null;
  }
};

export default QuestionInput;
